/**
 * CR453: Full UI Figma Seed Export
 * AppShell + AdminShell + DealCardV2 (primary + details, all deal types, all sizes, admin overlays)
 *
 * Rules:
 * - Use existing components as-is (no renames/refactors)
 * - Render real components (not images)
 * - Every sample MUST have a printed label with component identity + props
 * - No contract enforcement yet — structural render state only
 */

import React, { useMemo, useState } from "react";
import type { Deal } from "@/lib/types/deals";
import type { DealCategory } from "@/lib/deals/dealTypeRegistry";
import type { DealCardBlockType, DealCardBlockProps, ResolvedDealCardSpecV2, DealCardBlockSpec } from "@/lib/deal-cards/specV2/types";
import { DEFAULT_SPEC_V2 } from "@/lib/deal-cards/specV2/resolveSpec";
import { DealCardRendererV2 } from "@/components/deal-cards/DealCardRendererV2";
import { getBlockComponent } from "@/lib/deal-cards/blocks/registry";
import figmaContract from "../../design/figma/figma-contract.json";

// Block components
import { HeaderBlock } from "@/lib/deal-cards/blocks/HeaderBlock";
import { HeadlineBlock } from "@/lib/deal-cards/blocks/HeadlineBlock";
import { CTAButtonBlock } from "@/lib/deal-cards/blocks/CTAButtonBlock";
import { ValueBoxBlock } from "@/lib/deal-cards/blocks/ValueBoxBlock";
import { SpendBoxBlock } from "@/lib/deal-cards/blocks/SpendBoxBlock";
import { RewardBoxBlock } from "@/lib/deal-cards/blocks/RewardBoxBlock";
import { RibbonBlock } from "@/lib/deal-cards/blocks/RibbonBlock";
import { DatesBlock } from "@/lib/deal-cards/blocks/DatesBlock";
import { PromoBadgesBlock } from "@/lib/deal-cards/blocks/PromoBadgesBlock";
import { MetaPillsBlock } from "@/lib/deal-cards/blocks/MetaPillsBlock";
import { StarburstBlock } from "@/lib/deal-cards/blocks/StarburstBlock";
import { EligibilityBoxBlock } from "@/lib/deal-cards/blocks/EligibilityBoxBlock";
import { BackHeaderBlock } from "@/lib/deal-cards/blocks/BackHeaderBlock";
import { BackNavBlock } from "@/lib/deal-cards/blocks/BackNavBlock";
import { TermsBlock } from "@/lib/deal-cards/blocks/TermsBlock";
import { FinePrintBlock } from "@/lib/deal-cards/blocks/FinePrintBlock";
import { SourceBlock } from "@/lib/deal-cards/blocks/SourceBlock";
import { EvidenceBlock } from "@/lib/deal-cards/blocks/EvidenceBlock";
import { ValidatorBlock } from "@/lib/deal-cards/blocks/ValidatorBlock";
import { DetailsPillsBlock } from "@/lib/deal-cards/blocks/DetailsPillsBlock";
import { DetailsActionsBlock } from "@/lib/deal-cards/blocks/DetailsActionsBlock";
import { FlyerSnippetBlock } from "@/lib/deal-cards/blocks/FlyerSnippetBlock";
import { DetailsCTABlock } from "@/lib/deal-cards/blocks/DetailsCTABlock";
import { FeedbackBlock } from "@/lib/deal-cards/blocks/FeedbackBlock";

// UI primitives
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Separator } from "@/components/ui/separator";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerDescription,
  DrawerFooter,
} from "@/components/ui/drawer";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
  PaginationEllipsis,
} from "@/components/ui/pagination";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Progress } from "@/components/ui/progress";

// Icons
import {
  Search, Home, LayoutGrid, Settings, Users, Bell, ChevronRight,
  Package, BarChart3, Filter, Plus, Edit, Trash2, Eye, Download,
  ArrowLeft, Menu, X, CheckCircle2, AlertCircle, Info, Loader2,
  Store, Tag, Star, Percent, Gift, Ticket, SlidersHorizontal,
  ChevronDown, TrendingUp, Layers, FileText, Terminal
} from "lucide-react";

// ---------------------------------------------------------------
// Constants
// ---------------------------------------------------------------

type FigmaContractComponent = {
  type: "component" | "component_set";
  blockId?: string;
  required?: boolean;
  requiredChildren: string[];
};

type FigmaContract = {
  version: string;
  figma: { fileKey: string; libraryName: string };
  dealTypes: string[];
  variantKeys: string[];
  components: Record<string, FigmaContractComponent>;
};

const CONTRACT = figmaContract as FigmaContract;

const DEAL_TYPES: DealCategory[] = ["BONUS", "DISCOUNT", "POINTS", "IN_KIND", "UNCLASSIFIED"];

const PLACEHOLDER_BLOCK_IDS = new Set<DealCardBlockType>([
  "merchant_category_chips",
  "confidence_indicator",
  "debug_strip",
]);

const SEEDED_BLOCK_IDS = new Set<DealCardBlockType>([
  "header",
  "headline",
  "ribbon",
  "meta_pills",
  "value_box",
  "spend_box",
  "reward_box",
  "eligibility_box",
  "dates",
  "promo_badges",
  "starburst",
  "cta_button",
  "details_pills",
  "flyer_snippet",
  "details_cta",
  "merchant_category_chips",
  "confidence_indicator",
  "debug_strip",
  "feedback",
  "back_header",
  "back_nav",
  "terms",
  "fine_print",
  "source",
  "evidence",
  "validator",
  "details_actions",
  "flyer_snippet_details",
  "eligibility",
]);

const SIZES = ["compact", "regular", "large"] as const;
type Size = typeof SIZES[number];

const SIZE_WIDTH: Record<Size, string> = {
  compact: "280px",
  regular: "360px",
  large: "480px",
};

// ---------------------------------------------------------------
// Nav sections for the export page
// ---------------------------------------------------------------

const NAV_SECTIONS = [
  { id: "deal-card-v2", label: "DealCardV2" },
  { id: "app-shell", label: "AppShell" },
  { id: "admin-shell", label: "AdminShell" },
] as const;

// ---------------------------------------------------------------
// Seed Deal Factory
// ---------------------------------------------------------------

function makeSeedDeal(dealType: DealCategory, suffix = ""): Deal {
  const base: Deal = {
    merchant: "Metro",
    promo_merchant_name: "Metro",
    deal_type: dealType.toLowerCase() as Deal["deal_type"],
    deal_category: dealType,
    headline_mechanic: null,
    purchase_gift_card_brand: null,
    purchase_brand_logo_url: null,
    bonus_merchant: null,
    bonus_merchant_logo_url: null,
    bonus_amount: null,
    spend_amount: null,
    gift_card_brand: null,
    bonus_or_discount_amount: null,
    spend_range_min: null,
    spend_range_max: null,
    discount_percent: null,
    discount_tiers: null,
    deal_pricing_type: null,
    start_date: "2026-02-01",
    end_date: "2026-02-28",
    bonus_valid_from: null,
    bonus_valid_until: null,
    confidence_reason: "Seed data",
    raw_source_notes: "",
    requires_physical_card: false,
    claim_url: null,
    redeem_url: null,
    claim_deadline: null,
    campaign_name: null,
    fine_print: "See store for details. Offer valid Feb 1–28, 2026.",
    source_type: "flyer",
    channel: "in-store",
    region: "ON",
    proof_urls: [],
    confidence: "High",
  };

  switch (dealType) {
    case "BONUS":
      return {
        ...base,
        headline_mechanic: "Get $20 bonus when you spend $100",
        purchase_gift_card_brand: "Metro",
        bonus_amount: 20,
        spend_amount: 100,
        qualifying_spend_cad: 100,
        bonus_value_cad: 20,
        computed_value_cad: 20,
        effective_percent_back: 20,
      };
    case "DISCOUNT":
      return {
        ...base,
        headline_mechanic: "Save 20% on select gift cards",
        discount_percent: 20,
        qualifying_spend_cad: 50,
        computed_value_cad: 10,
        effective_percent_back: 20,
      };
    case "POINTS":
      return {
        ...base,
        headline_mechanic: "Earn 500 points when you buy $50",
        points_amount: 500,
        points_program_name: "PC Optimum",
        qualifying_spend_cad: 50,
        computed_value_cad: 5,
        effective_percent_back: 10,
      };
    case "IN_KIND":
      return {
        ...base,
        headline_mechanic: "Get a free coffee with $25 card",
        qualifying_spend_cad: 25,
        computed_value_cad: 5,
        effective_percent_back: 20,
      };
    default:
      return base;
  }
}

function buildResolvedSpec(dealType: DealCategory): ResolvedDealCardSpecV2 {
  return {
    ...DEFAULT_SPEC_V2,
    resolved: {
      variantKey: "standard_v2",
      dealTypeKey: dealType,
      labels: {},
      styleOverrides: {},
      usedFallback: true,
      sources: { variant: "fallback", overrides: "fallback" },
      components: {},
    },
  };
}

function makeBlock(type: string, id?: string): DealCardBlockSpec {
  return { id: id ?? type, type: type as never, enabled: true, order: 0 };
}

function RegistryBlockSample({
  type,
  deal,
  context,
}: {
  type: DealCardBlockType;
  deal: Deal;
  context: DealCardBlockProps<Deal>["context"];
}) {
  const BlockComponent = getBlockComponent(type);
  return <BlockComponent deal={deal} block={makeBlock(type)} context={context} />;
}

const defaultContext = {
  isAdmin: false,
  surface: "primary" as const,
  labels: {},
  styleOverrides: {},
};

const detailsContext = {
  isAdmin: false,
  surface: "details" as const,
  labels: {},
  styleOverrides: {},
};

const adminContext = {
  isAdmin: true,
  surface: "primary" as const,
  labels: {},
  styleOverrides: {},
};

// ---------------------------------------------------------------
// Layout helpers
// ---------------------------------------------------------------

function PageLabel({ children }: { children: React.ReactNode }) {
  return (
    <div className="text-[10px] font-mono text-muted-foreground/50 uppercase tracking-widest mb-2 select-none">
      {children}
    </div>
  );
}

function SectionTitle({ id, children }: { id?: string; children: React.ReactNode }) {
  return (
    <div id={id} className="mb-6 mt-16 scroll-mt-20">
      <div className="flex items-center gap-3">
        <div className="h-px flex-1 bg-border" />
        <h2 className="text-xl font-bold tracking-tight text-foreground px-3">{children}</h2>
        <div className="h-px flex-1 bg-border" />
      </div>
    </div>
  );
}

function SubSection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="mb-10">
      <h3 className="text-base font-semibold text-foreground mb-4 flex items-center gap-2">
        <span className="w-1.5 h-4 bg-primary rounded-full inline-block" />
        {title}
      </h3>
      {children}
    </div>
  );
}

function SampleCard({
  title,
  subtitle,
  children,
  width,
}: {
  title: string;
  subtitle?: string;
  children: React.ReactNode;
  width?: string;
}) {
  return (
    <div className="border border-border rounded-lg p-4 bg-card" style={width ? { width } : undefined}>
      <PageLabel>{title}</PageLabel>
      {subtitle && (
        <p className="text-[10px] font-mono text-muted-foreground/60 mb-3 leading-relaxed">{subtitle}</p>
      )}
      <div className="mt-2">{children}</div>
    </div>
  );
}

function Grid({ cols = 3, children }: { cols?: number; children: React.ReactNode }) {
  const colClass = {
    1: "grid-cols-1",
    2: "grid-cols-1 md:grid-cols-2",
    3: "grid-cols-1 md:grid-cols-2 lg:grid-cols-3",
    4: "grid-cols-1 md:grid-cols-2 lg:grid-cols-4",
  }[cols] ?? "grid-cols-1 md:grid-cols-3";
  return <div className={`grid ${colClass} gap-5 mb-6`}>{children}</div>;
}

// ---------------------------------------------------------------
// AppShell mock components
// ---------------------------------------------------------------

function MockAppHeader() {
  return (
    <header className="bg-card border-b border-border px-6 py-3 flex items-center gap-4">
      <div className="flex items-center gap-2">
        <div className="w-8 h-8 bg-primary/10 rounded flex items-center justify-center">
          <LayoutGrid className="h-4 w-4 text-primary" />
        </div>
        <span className="font-semibold text-foreground text-sm">Worth It</span>
      </div>
      <div className="flex-1 max-w-sm">
        <div className="flex items-center gap-2 border border-border rounded-md px-3 py-1.5 bg-background">
          <Search className="h-3.5 w-3.5 text-muted-foreground" />
          <span className="text-xs text-muted-foreground">Search deals…</span>
        </div>
      </div>
      <div className="ml-auto flex items-center gap-3">
        <Badge variant="outline" className="text-xs">Toronto, ON</Badge>
        <Button variant="ghost" size="icon" className="h-8 w-8">
          <Bell className="h-4 w-4" />
        </Button>
      </div>
    </header>
  );
}

function MockSearchBar() {
  return (
    <div className="flex gap-2">
      <div className="flex-1 flex items-center gap-2 border border-border rounded-md px-3 py-2 bg-background">
        <Search className="h-4 w-4 text-muted-foreground" />
        <span className="text-sm text-muted-foreground">Search gift card deals…</span>
      </div>
      <Button size="sm" className="shrink-0">Search</Button>
    </div>
  );
}

function MockCategoryBar() {
  const categories = ["All", "Grocery", "Gas & Auto", "Dining", "Travel", "Entertainment", "Retail", "Health"];
  return (
    <div className="flex items-center gap-2 overflow-x-auto pb-1">
      {categories.map((cat, i) => (
        <button
          key={cat}
          className={`shrink-0 px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
            i === 0 
              ? "bg-primary text-primary-foreground" 
              : "bg-muted text-muted-foreground hover:bg-accent"
          }`}
        >
          {cat}
        </button>
      ))}
    </div>
  );
}

function MockFilterBar() {
  return (
    <div className="flex items-center gap-2 flex-wrap">
      <Button variant="outline" size="sm" className="h-7 gap-1.5 text-xs">
        <SlidersHorizontal className="h-3 w-3" />
        Filters
      </Button>
      {["BONUS", "DISCOUNT", "POINTS"].map((type) => (
        <Badge key={type} variant="secondary" className="cursor-pointer text-xs gap-1">
          {type}
          <X className="h-2.5 w-2.5" />
        </Badge>
      ))}
      <span className="text-xs text-muted-foreground ml-auto">42 results</span>
    </div>
  );
}

function MockDealGrid() {
  return (
    <div className="grid grid-cols-2 gap-3">
      {[1, 2, 3, 4].map((i) => (
        <div key={i} className="border border-border rounded-xl p-3 bg-card flex flex-col gap-2">
          <div className="flex items-center gap-2">
            <Skeleton className="h-8 w-8 rounded" />
            <div className="flex-1">
              <Skeleton className="h-3 w-16 mb-1" />
              <Skeleton className="h-2.5 w-12" />
            </div>
          </div>
          <Skeleton className="h-3 w-full" />
          <Skeleton className="h-3 w-3/4" />
          <div className="flex gap-1 mt-1">
            <Skeleton className="h-5 w-12 rounded-full" />
            <Skeleton className="h-5 w-16 rounded-full" />
          </div>
        </div>
      ))}
    </div>
  );
}

function MockPagination() {
  return (
    <Pagination>
      <PaginationContent>
        <PaginationItem><PaginationPrevious href="#" /></PaginationItem>
        <PaginationItem><PaginationLink href="#">1</PaginationLink></PaginationItem>
        <PaginationItem><PaginationLink href="#" isActive>2</PaginationLink></PaginationItem>
        <PaginationItem><PaginationLink href="#">3</PaginationLink></PaginationItem>
        <PaginationItem><PaginationEllipsis /></PaginationItem>
        <PaginationItem><PaginationLink href="#">8</PaginationLink></PaginationItem>
        <PaginationItem><PaginationNext href="#" /></PaginationItem>
      </PaginationContent>
    </Pagination>
  );
}

function MockFooter() {
  return (
    <footer className="border-t border-border bg-muted/30 px-6 py-4">
      <div className="flex items-center justify-between text-xs text-muted-foreground">
        <span>© 2026 Worth It · All deals are user-sourced</span>
        <div className="flex gap-4">
          <a href="#" className="hover:text-foreground">Privacy</a>
          <a href="#" className="hover:text-foreground">Terms</a>
          <a href="#" className="hover:text-foreground">Contact</a>
        </div>
      </div>
    </footer>
  );
}

function MockEmptyState() {
  return (
    <div className="flex flex-col items-center justify-center py-12 text-center">
      <div className="w-12 h-12 bg-muted rounded-full flex items-center justify-center mb-4">
        <Search className="h-5 w-5 text-muted-foreground" />
      </div>
      <h3 className="font-semibold text-foreground mb-1">No deals found</h3>
      <p className="text-sm text-muted-foreground max-w-xs">
        Try adjusting your filters or search in a different category.
      </p>
      <Button variant="outline" size="sm" className="mt-4">Clear filters</Button>
    </div>
  );
}

function MockLoadingState() {
  return (
    <div className="grid grid-cols-2 gap-3">
      {[1, 2, 3, 4, 5, 6].map((i) => (
        <div key={i} className="border border-border rounded-xl p-4 bg-card animate-pulse">
          <div className="flex items-center gap-2 mb-3">
            <Skeleton className="h-10 w-10 rounded" />
            <div className="flex-1">
              <Skeleton className="h-3 w-20 mb-1.5" />
              <Skeleton className="h-2.5 w-14" />
            </div>
          </div>
          <Skeleton className="h-3 w-full mb-2" />
          <Skeleton className="h-3 w-4/5 mb-3" />
          <Skeleton className="h-7 w-full rounded-md" />
        </div>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------
// AdminShell mock components
// ---------------------------------------------------------------

function MockAdminTopNav() {
  return (
    <header className="bg-card border-b border-border px-5 py-2.5 flex items-center gap-4">
      <div className="flex items-center gap-2">
        <LayoutGrid className="h-4 w-4 text-primary" />
        <span className="text-sm font-semibold">Worth It Admin</span>
      </div>
      <Separator orientation="vertical" className="h-4" />
      <span className="text-xs text-muted-foreground">Deal Cards Editor</span>
      <div className="ml-auto flex items-center gap-2">
        <Badge variant="outline" className="text-xs gap-1">
          <span className="w-1.5 h-1.5 bg-[hsl(142,71%,45%)] rounded-full" />
          Live
        </Badge>
        <Avatar className="h-7 w-7">
          <AvatarFallback className="text-[10px]">AD</AvatarFallback>
        </Avatar>
      </div>
    </header>
  );
}

function MockAdminSideNav() {
  const items = [
    { icon: Home, label: "Dashboard", active: false },
    { icon: LayoutGrid, label: "Deal Cards", active: true },
    { icon: Store, label: "Merchants", active: false },
    { icon: Package, label: "Batch Runs", active: false },
    { icon: BarChart3, label: "Analytics", active: false },
    { icon: Settings, label: "Settings", active: false },
  ];
  return (
    <aside className="w-52 bg-muted/20 border-r border-border py-3 flex flex-col gap-1 px-2">
      {items.map(({ icon: Icon, label, active }) => (
        <button
          key={label}
          className={`flex items-center gap-2.5 px-3 py-2 rounded-md text-xs font-medium transition-colors ${
            active
              ? "bg-primary text-primary-foreground"
              : "text-muted-foreground hover:bg-muted hover:text-foreground"
          }`}
        >
          <Icon className="h-3.5 w-3.5" />
          {label}
        </button>
      ))}
    </aside>
  );
}

function MockAdminPageHeader() {
  return (
    <div className="flex items-center justify-between pb-4 border-b border-border mb-5">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" className="h-7 w-7">
          <ArrowLeft className="h-3.5 w-3.5" />
        </Button>
        <div>
          <h1 className="text-lg font-semibold text-foreground">Deal Cards Editor</h1>
          <p className="text-xs text-muted-foreground">Manage card templates and block configuration</p>
        </div>
      </div>
      <div className="flex items-center gap-2">
        <Button variant="outline" size="sm" className="h-7 text-xs gap-1.5">
          <Eye className="h-3 w-3" /> Preview
        </Button>
        <Button size="sm" className="h-7 text-xs gap-1.5">
          <Download className="h-3 w-3" /> Publish
        </Button>
      </div>
    </div>
  );
}

function MockAdminTabs() {
  return (
    <Tabs defaultValue="template">
      <TabsList>
        <TabsTrigger value="template">Template</TabsTrigger>
        <TabsTrigger value="components">Components</TabsTrigger>
        <TabsTrigger value="blocks">Blocks</TabsTrigger>
        <TabsTrigger value="preview">Preview</TabsTrigger>
      </TabsList>
      <TabsContent value="template" className="mt-3">
        <div className="text-xs text-muted-foreground p-3 bg-muted/30 rounded border border-border">
          Template configuration panel content goes here.
        </div>
      </TabsContent>
    </Tabs>
  );
}

function MockAdminPanel() {
  return (
    <div className="border border-border rounded-lg bg-card">
      <div className="px-4 py-3 border-b border-border flex items-center justify-between">
        <span className="text-sm font-medium">Block Toggles</span>
        <Badge variant="secondary" className="text-xs">8 enabled</Badge>
      </div>
      <div className="p-4 space-y-2.5">
        {["Header", "Ribbon", "Headline", "Value Box", "Spend Box", "CTA Button", "Dates", "Fine Print"].map(
          (block) => (
            <div key={block} className="flex items-center justify-between">
              <span className="text-xs text-foreground">{block}</span>
              <Switch defaultChecked className="h-4 w-7" />
            </div>
          )
        )}
      </div>
    </div>
  );
}

function MockAdminTable() {
  const rows = [
    { merchant: "Metro", type: "BONUS", value: "$20", status: "Live", updated: "2h ago" },
    { merchant: "Shoppers", type: "POINTS", value: "500 pts", status: "Live", updated: "1d ago" },
    { merchant: "Shell", type: "DISCOUNT", value: "15%", status: "Draft", updated: "3d ago" },
    { merchant: "Tim Hortons", type: "IN_KIND", value: "Free drink", status: "Archived", updated: "1w ago" },
  ];
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead className="text-xs">Merchant</TableHead>
          <TableHead className="text-xs">Type</TableHead>
          <TableHead className="text-xs">Value</TableHead>
          <TableHead className="text-xs">Status</TableHead>
          <TableHead className="text-xs">Updated</TableHead>
          <TableHead className="text-xs text-right">Actions</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.map((row) => (
          <TableRow key={row.merchant}>
            <TableCell className="text-xs font-medium">{row.merchant}</TableCell>
            <TableCell>
              <Badge variant="outline" className="text-[10px]">{row.type}</Badge>
            </TableCell>
            <TableCell className="text-xs">{row.value}</TableCell>
            <TableCell>
              <Badge
                className="text-[10px]"
                variant={
                  row.status === "Live"
                    ? "default"
                    : row.status === "Draft"
                    ? "secondary"
                    : "outline"
                }
              >
                {row.status}
              </Badge>
            </TableCell>
            <TableCell className="text-xs text-muted-foreground">{row.updated}</TableCell>
            <TableCell className="text-right">
              <div className="flex gap-1 justify-end">
                <Button variant="ghost" size="icon" className="h-6 w-6">
                  <Edit className="h-3 w-3" />
                </Button>
                <Button variant="ghost" size="icon" className="h-6 w-6">
                  <Trash2 className="h-3 w-3 text-destructive" />
                </Button>
              </div>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

function MockAdminForm() {
  return (
    <form className="space-y-4">
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1.5">
          <Label className="text-xs">Merchant</Label>
          <Input placeholder="Metro" className="h-8 text-sm" />
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs">Deal Type</Label>
          <Select>
            <SelectTrigger className="h-8 text-sm"><SelectValue placeholder="BONUS" /></SelectTrigger>
            <SelectContent>
              {["BONUS", "DISCOUNT", "POINTS", "IN_KIND"].map((t) => (
                <SelectItem key={t} value={t}>{t}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
      <div className="space-y-1.5">
        <Label className="text-xs">Headline</Label>
        <Input placeholder="Get $20 bonus when you spend $100" className="h-8 text-sm" />
      </div>
      <div className="space-y-1.5">
        <Label className="text-xs">Fine Print</Label>
        <Textarea placeholder="Terms and conditions…" className="text-sm min-h-[64px]" />
      </div>
      <div className="flex items-center gap-2">
        <Checkbox id="active" />
        <Label htmlFor="active" className="text-xs cursor-pointer">Active / Published</Label>
      </div>
      <div className="flex gap-2 pt-1">
        <Button type="submit" size="sm" className="h-7 text-xs">Save Changes</Button>
        <Button type="button" variant="outline" size="sm" className="h-7 text-xs">Cancel</Button>
      </div>
    </form>
  );
}

function MockAdminInspector() {
  return (
    <div className="border border-border rounded-lg bg-card text-xs font-mono">
      <div className="px-3 py-2 border-b border-border flex items-center gap-1.5 bg-muted/30">
        <Terminal className="h-3 w-3 text-muted-foreground" />
        <span className="text-muted-foreground">Inspector — deal_id: seed-BONUS-preview</span>
      </div>
      <div className="p-3 space-y-1 text-muted-foreground leading-relaxed">
        <div><span className="text-primary">deal_type</span>: "BONUS"</div>
        <div><span className="text-primary">variant_key</span>: "standard_v2"</div>
        <div><span className="text-primary">surface</span>: "primary"</div>
        <div><span className="text-primary">blocks_enabled</span>: 8</div>
        <div><span className="text-primary">fingerprint</span>: "a3f7c2"</div>
        <div><span className="text-primary">render_version</span>: "v2"</div>
        <div><span className="text-primary">contract_version</span>: "v2_render_deal_v1"</div>
      </div>
    </div>
  );
}

function MockAdminPreviewPane() {
  const deal = makeSeedDeal("BONUS", "preview-pane");
  const spec = buildResolvedSpec("BONUS");
  return (
    <div className="border border-border rounded-lg bg-muted/10 overflow-hidden">
      <div className="px-3 py-2 border-b border-border flex items-center justify-between bg-card">
        <span className="text-xs font-medium">Full Card Preview</span>
        <div className="flex gap-1">
          <Badge variant="outline" className="text-[10px]">V2</Badge>
          <Badge variant="secondary" className="text-[10px]">primary</Badge>
        </div>
      </div>
      <div className="p-4 flex justify-center">
        <div style={{ maxWidth: "320px", width: "100%" }}>
          <DealCardRendererV2
            deal={{ ...deal, id: "seed-BONUS-preview-pane" }}
            spec={spec}
            isAdmin={true}
            surface="primary"
          />
        </div>
      </div>
    </div>
  );
}

function MockAdminEmptyState() {
  return (
    <div className="flex flex-col items-center justify-center py-10 text-center border border-dashed border-border rounded-lg bg-muted/10">
      <FileText className="h-8 w-8 text-muted-foreground mb-3" />
      <p className="text-sm font-medium text-foreground">No templates configured</p>
      <p className="text-xs text-muted-foreground mt-1">Create your first deal card template to get started.</p>
      <Button size="sm" className="mt-4 gap-1.5 h-7 text-xs">
        <Plus className="h-3 w-3" /> New Template
      </Button>
    </div>
  );
}

function MockAdminLoadingState() {
  return (
    <div className="space-y-2">
      {[1, 2, 3].map((i) => (
        <div key={i} className="flex items-center gap-3 p-3 border border-border rounded-lg animate-pulse">
          <Skeleton className="h-9 w-9 rounded" />
          <div className="flex-1 space-y-1.5">
            <Skeleton className="h-3 w-40" />
            <Skeleton className="h-2.5 w-24" />
          </div>
          <Skeleton className="h-6 w-14 rounded-full" />
        </div>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------
// Toast mock
// ---------------------------------------------------------------

function MockToast({ variant = "default" }: { variant?: "default" | "success" | "error" | "info" }) {
  const configs = {
    default: { icon: <Bell className="h-4 w-4" />, title: "Notification", msg: "Your changes have been saved." },
    success: { icon: <CheckCircle2 className="h-4 w-4 text-[hsl(142,71%,45%)]" />, title: "Success", msg: "Deal card published successfully." },
    error: { icon: <AlertCircle className="h-4 w-4 text-destructive" />, title: "Error", msg: "Failed to save. Please try again." },
    info: { icon: <Info className="h-4 w-4 text-[hsl(217,91%,60%)]" />, title: "Info", msg: "Spec fingerprint has changed since last save." },
  };
  const { icon, title, msg } = configs[variant];
  return (
    <div className="border border-border rounded-lg bg-card shadow-sm px-4 py-3 flex items-start gap-3 max-w-[320px]">
      {icon}
      <div className="flex-1">
        <p className="text-sm font-medium">{title}</p>
        <p className="text-xs text-muted-foreground mt-0.5">{msg}</p>
      </div>
      <button className="text-muted-foreground hover:text-foreground">
        <X className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}

// ---------------------------------------------------------------
// Dialog / Drawer mock hooks
// ---------------------------------------------------------------

function DialogSample() {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button size="sm" className="h-7 text-xs" onClick={() => setOpen(true)}>Open Dialog</Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Publish Deal Card</DialogTitle>
            <DialogDescription>
              This will push the current template configuration live. Are you sure?
            </DialogDescription>
          </DialogHeader>
          <div className="py-2">
            <div className="text-xs text-muted-foreground bg-muted/40 rounded p-3 font-mono">
              variant: standard_v2 · deal_type: BONUS · surface: primary
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => setOpen(false)}>Cancel</Button>
            <Button size="sm" onClick={() => setOpen(false)}>Confirm Publish</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

function DrawerSample() {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => setOpen(true)}>Open Drawer</Button>
      <Drawer open={open} onOpenChange={setOpen}>
        <DrawerContent>
          <DrawerHeader>
            <DrawerTitle>Deal Details</DrawerTitle>
            <DrawerDescription>Full breakdown of the selected deal record.</DrawerDescription>
          </DrawerHeader>
          <div className="px-4 pb-4 space-y-2 text-xs text-muted-foreground font-mono">
            <p>merchant: Metro</p>
            <p>deal_type: BONUS</p>
            <p>bonus_amount: $20</p>
            <p>qualifying_spend_cad: $100</p>
          </div>
          <DrawerFooter>
            <Button size="sm" onClick={() => setOpen(false)}>Close</Button>
          </DrawerFooter>
        </DrawerContent>
      </Drawer>
    </>
  );
}

// ---------------------------------------------------------------
// Page
// ---------------------------------------------------------------

export default function FigmaSeedPage() {
  const [activeSection, setActiveSection] = useState<string>("deal-card-v2");
  const contractEntries = useMemo(
    () => Object.entries(CONTRACT.components).sort((a, b) => a[0].localeCompare(b[0])),
    []
  );

  return (
    <div className="min-h-screen bg-background">
      {/* Sticky top nav */}
      <div className="sticky top-0 z-40 bg-background border-b border-border">
        <div className="max-w-[1600px] mx-auto px-6 py-3 flex items-center gap-6">
          <div>
            <h1 className="text-sm font-bold text-foreground">CR453 · Figma Seed Export</h1>
            <p className="text-[10px] font-mono text-muted-foreground/60">AppShell + AdminShell + DealCardV2 — structural render state</p>
          </div>
          <div className="ml-auto flex gap-1">
            {NAV_SECTIONS.map((s) => (
              <a
                key={s.id}
                href={`#${s.id}`}
                onClick={() => setActiveSection(s.id)}
                className={`px-3 py-1.5 rounded text-xs font-medium transition-colors ${
                  activeSection === s.id
                    ? "bg-primary text-primary-foreground"
                    : "text-muted-foreground hover:bg-muted"
                }`}
              >
                {s.label}
              </a>
            ))}
          </div>
        </div>
      </div>

      <div className="max-w-[1600px] mx-auto px-6 pb-20">

        {/* ================================================================
            SECTION 1: DealCardV2
        ================================================================ */}
        <SectionTitle id="deal-card-v2">§1 — DealCardV2</SectionTitle>

        {/* 1A: Contract coverage */}
        <SubSection title="1A · Contract Coverage">
          <div className="border border-border rounded-lg p-4 bg-card mb-5">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs">
              <p><span className="font-semibold">Version:</span> {CONTRACT.version}</p>
              <p><span className="font-semibold">File Key:</span> {CONTRACT.figma.fileKey}</p>
              <p><span className="font-semibold">Library:</span> {CONTRACT.figma.libraryName}</p>
              <p><span className="font-semibold">Variant Keys:</span> {CONTRACT.variantKeys.join(", ")}</p>
              <p className="md:col-span-2"><span className="font-semibold">Deal Types:</span> {CONTRACT.dealTypes.join(", ")}</p>
              <p className="md:col-span-2"><span className="font-semibold">Components:</span> {contractEntries.length}</p>
            </div>
          </div>
          <div className="border border-border rounded-lg overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Component Key</TableHead>
                  <TableHead>Block ID</TableHead>
                  <TableHead>Required</TableHead>
                  <TableHead>Required Children</TableHead>
                  <TableHead>Coverage</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {contractEntries.map(([key, component]) => {
                  const blockId = component.blockId as DealCardBlockType | undefined;
                  const coverage = !blockId && key === "WI/DealCardV2/Card"
                    ? "seeded"
                    : blockId && PLACEHOLDER_BLOCK_IDS.has(blockId) && SEEDED_BLOCK_IDS.has(blockId)
                      ? "placeholder"
                      : blockId && SEEDED_BLOCK_IDS.has(blockId)
                        ? "seeded"
                        : "missing";
                  return (
                    <TableRow key={key}>
                      <TableCell className="font-mono text-xs">{key}</TableCell>
                      <TableCell className="font-mono text-xs">{component.blockId ?? "—"}</TableCell>
                      <TableCell className="text-xs">{component.required === true ? "yes" : "no"}</TableCell>
                      <TableCell className="text-xs">
                        {component.requiredChildren.length > 0 ? component.requiredChildren.join(", ") : "—"}
                      </TableCell>
                      <TableCell>
                        <Badge
                          variant={
                            coverage === "seeded"
                              ? "default"
                              : coverage === "placeholder"
                                ? "secondary"
                                : "destructive"
                          }
                          className="text-[10px] uppercase"
                        >
                          {coverage}
                        </Badge>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        </SubSection>

        {/* 1B: Primary surface — all deal types × all sizes */}
        <SubSection title="1B · Primary Surface — Deal Types × Sizes">
          {DEAL_TYPES.map((dealType) => (
            <div key={dealType} className="mb-8">
              <h4 className="text-xs font-mono text-muted-foreground uppercase tracking-wider mb-3">
                deal_type={dealType}
              </h4>
              <div className="flex flex-wrap gap-5">
                {SIZES.map((size) => {
                  const deal = makeSeedDeal(dealType, size);
                  const spec = buildResolvedSpec(dealType);
                  return (
                    <SampleCard
                      key={`${dealType}-${size}`}
                      title="DealCardRendererV2"
                      subtitle={`deal_type=${dealType} · size=${size} · surface=primary · width=${SIZE_WIDTH[size]}`}
                    >
                      <div style={{ width: SIZE_WIDTH[size] }}>
                        <DealCardRendererV2
                          deal={{ ...deal, id: `seed-${dealType}-${size}` }}
                          spec={spec}
                          isAdmin={false}
                          surface="primary"
                        />
                      </div>
                    </SampleCard>
                  );
                })}
              </div>
            </div>
          ))}
        </SubSection>

        {/* 1C: Details surface */}
        <SubSection title="1C · Details Surface — Deal Types">
          <Grid cols={2}>
            {DEAL_TYPES.map((dealType) => {
              const deal = makeSeedDeal(dealType, "details");
              const spec = buildResolvedSpec(dealType);
              return (
                <SampleCard
                  key={dealType}
                  title="DealCardRendererV2"
                  subtitle={`deal_type=${dealType} · surface=details`}
                >
                  <DealCardRendererV2
                    deal={{ ...deal, id: `seed-${dealType}-details` }}
                    spec={spec}
                    isAdmin={false}
                    surface="details"
                  />
                </SampleCard>
              );
            })}
          </Grid>
        </SubSection>

        {/* 1D: Admin overlays */}
        <SubSection title="1D · Admin Overlays — isAdmin=true + DealIdBadge">
          <Grid cols={2}>
            {DEAL_TYPES.map((dealType) => {
              const deal = makeSeedDeal(dealType, "admin");
              const spec = buildResolvedSpec(dealType);
              return (
                <SampleCard
                  key={dealType}
                  title="DealCardRendererV2"
                  subtitle={`deal_type=${dealType} · isAdmin=true · surface=primary · shows DealIdBadge`}
                >
                  <div style={{ maxWidth: SIZE_WIDTH["regular"] }}>
                    <DealCardRendererV2
                      deal={{ ...deal, id: `seed-${dealType}-admin` }}
                      spec={spec}
                      isAdmin={true}
                      surface="primary"
                    />
                  </div>
                </SampleCard>
              );
            })}
          </Grid>
        </SubSection>

        {/* 1E: Standalone blocks */}
        <SubSection title="1E · Standalone Blocks — Primary Surface">
          <Grid cols={3}>
            <SampleCard title="HeaderBlock" subtitle="type=header · surface=primary">
              <HeaderBlock deal={makeSeedDeal("BONUS", "hdr")} block={makeBlock("header")} context={defaultContext} />
            </SampleCard>
            <SampleCard title="HeadlineBlock" subtitle="type=headline · BONUS">
              <HeadlineBlock deal={makeSeedDeal("BONUS", "hl")} block={makeBlock("headline")} context={defaultContext} />
            </SampleCard>
            <SampleCard title="RibbonBlock" subtitle="type=ribbon · BONUS">
              <RibbonBlock deal={makeSeedDeal("BONUS", "rib")} block={makeBlock("ribbon")} context={defaultContext} />
            </SampleCard>
            <SampleCard title="MetaPillsBlock" subtitle="type=meta_pills · DISCOUNT">
              <MetaPillsBlock deal={makeSeedDeal("DISCOUNT", "mp")} block={makeBlock("meta_pills")} context={defaultContext} />
            </SampleCard>
            <SampleCard title="ValueBoxBlock" subtitle="type=value_box · BONUS">
              <ValueBoxBlock deal={makeSeedDeal("BONUS", "vb")} block={makeBlock("value_box")} context={defaultContext} />
            </SampleCard>
            <SampleCard title="SpendBoxBlock" subtitle="type=spend_box · BONUS">
              <SpendBoxBlock deal={makeSeedDeal("BONUS", "sb")} block={makeBlock("spend_box")} context={defaultContext} />
            </SampleCard>
            <SampleCard title="RewardBoxBlock" subtitle="type=reward_box · POINTS">
              <RewardBoxBlock deal={makeSeedDeal("POINTS", "rb")} block={makeBlock("reward_box")} context={defaultContext} />
            </SampleCard>
            <SampleCard title="EligibilityBoxBlock" subtitle="type=eligibility_box · BONUS">
              <EligibilityBoxBlock deal={makeSeedDeal("BONUS", "eb")} block={makeBlock("eligibility_box")} context={defaultContext} />
            </SampleCard>
            <SampleCard title="DatesBlock" subtitle="type=dates · BONUS">
              <DatesBlock deal={makeSeedDeal("BONUS", "dt")} block={makeBlock("dates")} context={defaultContext} />
            </SampleCard>
            <SampleCard title="PromoBadgesBlock" subtitle="type=promo_badges · DISCOUNT">
              <PromoBadgesBlock deal={makeSeedDeal("DISCOUNT", "pb")} block={makeBlock("promo_badges")} context={defaultContext} />
            </SampleCard>
            <SampleCard title="StarburstBlock" subtitle="type=starburst · BONUS">
              <StarburstBlock deal={makeSeedDeal("BONUS", "stb")} block={makeBlock("starburst")} context={defaultContext} />
            </SampleCard>
            <SampleCard title="CTAButtonBlock" subtitle="type=cta_button · BONUS">
              <CTAButtonBlock deal={makeSeedDeal("BONUS", "cta")} block={makeBlock("cta_button")} context={defaultContext} />
            </SampleCard>
            <SampleCard title="DetailsPillsBlock" subtitle="type=details_pills · BONUS">
              <DetailsPillsBlock deal={makeSeedDeal("BONUS", "dp")} block={makeBlock("details_pills")} context={defaultContext} />
            </SampleCard>
            <SampleCard title="FlyerSnippetBlock" subtitle="type=flyer_snippet · BONUS">
              <FlyerSnippetBlock deal={makeSeedDeal("BONUS", "fs")} block={makeBlock("flyer_snippet")} context={defaultContext} />
            </SampleCard>
            <SampleCard title="DetailsCTABlock" subtitle="type=details_cta · BONUS">
              <DetailsCTABlock deal={makeSeedDeal("BONUS", "dcta")} block={makeBlock("details_cta")} context={defaultContext} />
            </SampleCard>
            <SampleCard title="MerchantCategoryChips" subtitle="type=merchant_category_chips · placeholder">
              <RegistryBlockSample type="merchant_category_chips" deal={makeSeedDeal("BONUS", "mcc")} context={defaultContext} />
            </SampleCard>
            <SampleCard title="ConfidenceIndicator" subtitle="type=confidence_indicator · placeholder">
              <RegistryBlockSample type="confidence_indicator" deal={makeSeedDeal("BONUS", "ci")} context={adminContext} />
            </SampleCard>
            <SampleCard title="DebugStrip" subtitle="type=debug_strip · placeholder">
              <RegistryBlockSample type="debug_strip" deal={makeSeedDeal("BONUS", "ds")} context={adminContext} />
            </SampleCard>
            <SampleCard title="FeedbackBlock" subtitle="type=feedback · admin-safe seed sample">
              <FeedbackBlock deal={makeSeedDeal("BONUS", "fb")} block={makeBlock("feedback")} context={adminContext} />
            </SampleCard>
          </Grid>
        </SubSection>

        {/* 1F: Standalone blocks — Details surface */}
        <SubSection title="1F · Standalone Blocks — Details Surface">
          <Grid cols={3}>
            <SampleCard title="BackHeaderBlock" subtitle="type=back_header · surface=details">
              <BackHeaderBlock deal={makeSeedDeal("BONUS", "bh")} block={makeBlock("back_header")} context={detailsContext} />
            </SampleCard>
            <SampleCard title="BackNavBlock" subtitle="type=back_nav · surface=details">
              <BackNavBlock deal={makeSeedDeal("BONUS", "bn")} block={makeBlock("back_nav")} context={detailsContext} />
            </SampleCard>
            <SampleCard title="TermsBlock" subtitle="type=terms · BONUS">
              <TermsBlock deal={makeSeedDeal("BONUS", "tm")} block={makeBlock("terms")} context={detailsContext} />
            </SampleCard>
            <SampleCard title="FinePrintBlock" subtitle="type=fine_print · BONUS">
              <FinePrintBlock deal={makeSeedDeal("BONUS", "fp")} block={makeBlock("fine_print")} context={detailsContext} />
            </SampleCard>
            <SampleCard title="SourceBlock" subtitle="type=source · BONUS">
              <SourceBlock deal={makeSeedDeal("BONUS", "src")} block={makeBlock("source")} context={detailsContext} />
            </SampleCard>
            <SampleCard title="EvidenceBlock" subtitle="type=evidence · BONUS">
              <EvidenceBlock
                deal={{ ...makeSeedDeal("BONUS", "ev"), proof_urls: ["https://example.com/proof.png"] }}
                block={makeBlock("evidence")}
                context={detailsContext}
              />
            </SampleCard>
            <SampleCard title="ValidatorBlock" subtitle="type=validator · BONUS">
              <ValidatorBlock deal={makeSeedDeal("BONUS", "vld")} block={makeBlock("validator")} context={detailsContext} />
            </SampleCard>
            <SampleCard title="DetailsActionsBlock" subtitle="type=details_actions · BONUS">
              <DetailsActionsBlock deal={makeSeedDeal("BONUS", "da")} block={makeBlock("details_actions")} context={detailsContext} />
            </SampleCard>
            <SampleCard title="FlyerSnippetDetailsBlock" subtitle="type=flyer_snippet_details · BONUS">
              <FlyerSnippetBlock deal={makeSeedDeal("BONUS", "fsd")} block={makeBlock("flyer_snippet_details")} context={detailsContext} />
            </SampleCard>
            <SampleCard title="EligibilityBoxBlock (details)" subtitle="type=eligibility_box · surface=details">
              <EligibilityBoxBlock deal={makeSeedDeal("BONUS", "ebd")} block={makeBlock("eligibility_box")} context={detailsContext} />
            </SampleCard>
          </Grid>
        </SubSection>


        {/* ================================================================
            SECTION 2: AppShell
        ================================================================ */}
        <SectionTitle id="app-shell">§2 — AppShell</SectionTitle>

        <SubSection title="2A · Header">
          <SampleCard title="AppHeader" subtitle="site header with logo, search, location badge, notification bell">
            <MockAppHeader />
          </SampleCard>
        </SubSection>

        <SubSection title="2B · SearchBar">
          <SampleCard title="SearchBar" subtitle="full-width search input with submit CTA">
            <MockSearchBar />
          </SampleCard>
        </SubSection>

        <SubSection title="2C · CategoryBar">
          <SampleCard title="CategoryBar" subtitle="horizontal scrollable chip row — first chip is active">
            <MockCategoryBar />
          </SampleCard>
        </SubSection>

        <SubSection title="2D · FilterBar">
          <SampleCard title="FilterBar" subtitle="filter trigger + active filter badges + results count">
            <MockFilterBar />
          </SampleCard>
        </SubSection>

        <SubSection title="2E · DealGrid">
          <SampleCard title="DealGrid" subtitle="2-col card grid in loading/skeleton state">
            <MockDealGrid />
          </SampleCard>
        </SubSection>

        <SubSection title="2F · Pagination">
          <SampleCard title="Pagination" subtitle="numbered page nav with prev/next/ellipsis">
            <MockPagination />
          </SampleCard>
        </SubSection>

        <SubSection title="2G · Footer">
          <SampleCard title="Footer" subtitle="copyright + legal links">
            <MockFooter />
          </SampleCard>
        </SubSection>

        <SubSection title="2H · Modal + Drawer">
          <Grid cols={2}>
            <SampleCard title="Dialog / Modal" subtitle="confirmation dialog — publish deal card flow">
              <DialogSample />
            </SampleCard>
            <SampleCard title="Drawer" subtitle="bottom sheet — deal details breakdown">
              <DrawerSample />
            </SampleCard>
          </Grid>
        </SubSection>

        <SubSection title="2I · EmptyState + LoadingState">
          <Grid cols={2}>
            <SampleCard title="EmptyState" subtitle="no results — search/filter cleared">
              <MockEmptyState />
            </SampleCard>
            <SampleCard title="LoadingState" subtitle="grid skeleton — data fetching">
              <MockLoadingState />
            </SampleCard>
          </Grid>
        </SubSection>


        {/* ================================================================
            SECTION 3: AdminShell
        ================================================================ */}
        <SectionTitle id="admin-shell">§3 — AdminShell</SectionTitle>

        <SubSection title="3A · TopNav">
          <SampleCard title="AdminTopNav" subtitle="breadcrumb + environment badge + avatar">
            <MockAdminTopNav />
          </SampleCard>
        </SubSection>

        <SubSection title="3B · SideNav">
          <SampleCard title="AdminSideNav" subtitle="icon + label nav list — Deal Cards active">
            <div style={{ height: "260px" }}>
              <MockAdminSideNav />
            </div>
          </SampleCard>
        </SubSection>

        <SubSection title="3C · PageHeader">
          <SampleCard title="AdminPageHeader" subtitle="title + description + action buttons (Preview / Publish)">
            <MockAdminPageHeader />
          </SampleCard>
        </SubSection>

        <SubSection title="3D · Tabs">
          <SampleCard title="AdminTabs" subtitle="Template / Components / Blocks / Preview">
            <MockAdminTabs />
          </SampleCard>
        </SubSection>

        <SubSection title="3E · Panel">
          <Grid cols={2}>
            <SampleCard title="AdminPanel" subtitle="block toggles panel — 8 enabled">
              <MockAdminPanel />
            </SampleCard>
            <SampleCard title="AdminInspector" subtitle="monospace debug inspector — deal metadata">
              <MockAdminInspector />
            </SampleCard>
          </Grid>
        </SubSection>

        <SubSection title="3F · Table">
          <SampleCard title="AdminTable" subtitle="deal rows with type badge, status, actions">
            <MockAdminTable />
          </SampleCard>
        </SubSection>

        <SubSection title="3G · Form">
          <SampleCard title="AdminForm" subtitle="deal record edit form — merchant, type, headline, fine print, active toggle">
            <MockAdminForm />
          </SampleCard>
        </SubSection>

        <SubSection title="3H · PreviewPane">
          <SampleCard title="AdminPreviewPane" subtitle="full card preview pane — DealCardRendererV2 inside admin container">
            <MockAdminPreviewPane />
          </SampleCard>
        </SubSection>

        <SubSection title="3I · Dialog + Toast">
          <Grid cols={2}>
            <SampleCard title="AdminDialog" subtitle="confirm publish — deal card flow">
              <DialogSample />
            </SampleCard>
            <div className="space-y-3">
              {(["default", "success", "error", "info"] as const).map((v) => (
                <SampleCard key={v} title={`Toast · ${v}`} subtitle={`variant=${v}`}>
                  <MockToast variant={v} />
                </SampleCard>
              ))}
            </div>
          </Grid>
        </SubSection>

        <SubSection title="3J · EmptyState + LoadingState (Admin)">
          <Grid cols={2}>
            <SampleCard title="AdminEmptyState" subtitle="no templates — create CTA">
              <MockAdminEmptyState />
            </SampleCard>
            <SampleCard title="AdminLoadingState" subtitle="deal rows loading skeleton">
              <MockAdminLoadingState />
            </SampleCard>
          </Grid>
        </SubSection>

        {/* Progress indicator */}
        <SubSection title="3K · Progress">
          <Grid cols={2}>
            <SampleCard title="Progress · 65%" subtitle="batch run progress indicator">
              <div className="space-y-2">
                <div className="flex justify-between text-xs text-muted-foreground">
                  <span>Processing deals…</span>
                  <span>65%</span>
                </div>
                <Progress value={65} className="h-2" />
              </div>
            </SampleCard>
            <SampleCard title="Progress · Indeterminate" subtitle="generic loading state">
              <div className="space-y-2">
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  <Loader2 className="h-3 w-3 animate-spin" />
                  <span>Resolving spec…</span>
                </div>
                <Progress value={undefined} className="h-2" />
              </div>
            </SampleCard>
          </Grid>
        </SubSection>

        {/* Footer spacer */}
        <div className="h-20 flex items-center justify-center">
          <p className="text-xs font-mono text-muted-foreground/40">
            CR453 · Full UI Figma Seed Export · {new Date().toISOString().split("T")[0]}
          </p>
        </div>
      </div>
    </div>
  );
}
